import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Pencil, Trash2, CheckCircle2, Ban, RotateCcw, Search } from "lucide-react";
import { toast } from "sonner";
import { apiCall } from "@/components/admin/people/peopleUtils";

const EMPTY = { name: "", school_code: "", email: "", phone: "", city: "", state: "", address: "", status: "pending" };

export default function AdminSchools() {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = [`limit=100`];
      if (statusFilter !== "all") params.push(`status=${statusFilter}`);
      if (search) params.push(`search=${encodeURIComponent(search)}`);
      const d = await apiCall(`/admin/schools?${params.join("&")}`);
      setSchools(d.schools || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const openCreate = () => { setForm(EMPTY); setModal({ mode: "create" }); };
  const openEdit = (s) => { setForm({ name: s.name || "", school_code: s.school_code || "", email: s.email || "", phone: s.phone || "", city: s.city || "", state: s.state || "", address: s.address || "", status: s.status || "pending" }); setModal({ mode: "edit", data: s }); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("School name is required"); return; }
    setSaving(true);
    try {
      if (modal.mode === "create") {
        await apiCall("/admin/people/schools", { method: "POST", body: form });
        toast.success("School created");
      } else {
        const { school_code, ...rest } = form; // code immutable on edit
        await apiCall(`/admin/people/schools/${modal.data.id}`, { method: "PUT", body: rest });
        toast.success("School updated");
      }
      setModal(null); load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const act = async (s, action) => {
    try {
      if (action === "delete") {
        if (!window.confirm(`Delete ${s.name}? This also removes its students & teachers. This cannot be undone.`)) return;
        await apiCall(`/admin/people/schools/${s.id}`, { method: "DELETE" });
        toast.success("School deleted");
      } else {
        const body = action === "verify" ? { action: "approve" } : undefined;
        await apiCall(`/admin/schools/${s.id}/${action}`, { method: "PUT", body });
        toast.success(`School ${action === "verify" ? "verified" : action === "suspend" ? "suspended" : "reactivated"}`);
      }
      load();
    } catch (e) { toast.error(e.message); }
  };

  const statusBadge = (st) => {
    const map = { verified: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700", suspended: "bg-red-100 text-red-700" };
    return <Badge className={map[st] || "bg-slate-200 text-slate-600"}>{st || "pending"}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="admin-schools-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Building2 className="h-6 w-6 text-purple-600" /> Schools</h1>
          <p className="text-sm text-slate-500">Manage B2B school partnerships and enrollments</p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="school-create-btn"><Plus className="h-4 w-4 mr-2" /> Add School</Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search schools" className="pl-10" data-testid="schools-search" />
        </div>
        <select className="border border-slate-200 rounded-md p-2 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="schools-status-filter">
          <option value="all">All statuses</option><option value="verified">Verified</option><option value="pending">Pending</option><option value="suspended">Suspended</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr><th className="p-4">Name</th><th className="p-4">Code</th><th className="p-4">Location</th><th className="p-4">Students</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {loading ? (<tr><td colSpan={6} className="p-8 text-center text-slate-400">Loading…</td></tr>)
              : schools.length === 0 ? (<tr><td colSpan={6} className="p-8 text-center text-slate-400">No schools found</td></tr>)
              : schools.map((s) => (
                <tr key={s.id} className="border-t border-slate-100" data-testid={`school-row-${s.id}`}>
                  <td className="p-4 font-medium text-slate-800">{s.name}</td>
                  <td className="p-4 text-slate-600 font-mono text-xs">{s.school_code}</td>
                  <td className="p-4 text-slate-600">{[s.city, s.state].filter(Boolean).join(", ") || "—"}</td>
                  <td className="p-4 text-slate-600">{s.total_students ?? 0}</td>
                  <td className="p-4">{statusBadge(s.status)}</td>
                  <td className="p-4">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" title="Edit" onClick={() => openEdit(s)} data-testid={`school-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                      {s.status !== "verified" && <Button size="sm" variant="ghost" title="Verify" className="text-emerald-600 hover:bg-emerald-50" onClick={() => act(s, "verify")} data-testid={`school-verify-${s.id}`}><CheckCircle2 className="h-4 w-4" /></Button>}
                      {s.status === "verified" && <Button size="sm" variant="ghost" title="Suspend" className="text-amber-600 hover:bg-amber-50" onClick={() => act(s, "suspend")} data-testid={`school-suspend-${s.id}`}><Ban className="h-4 w-4" /></Button>}
                      {s.status === "suspended" && <Button size="sm" variant="ghost" title="Reactivate" className="text-blue-600 hover:bg-blue-50" onClick={() => act(s, "reactivate")} data-testid={`school-reactivate-${s.id}`}><RotateCcw className="h-4 w-4" /></Button>}
                      <Button size="sm" variant="ghost" title="Delete" className="text-red-600 hover:bg-red-50" onClick={() => act(s, "delete")} data-testid={`school-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent data-testid="school-modal">
          <DialogHeader><DialogTitle>{modal?.mode === "create" ? "Add School" : "Edit School"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="School name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="school-name-input" />
            {modal?.mode === "create" && <Input placeholder="School code (auto if blank)" value={form.school_code} onChange={(e) => setForm({ ...form, school_code: e.target.value })} data-testid="school-code-input" />}
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              <Input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </div>
            <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <select className="w-full border border-slate-200 rounded-md p-2 text-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="school-status-select">
              <option value="pending">Pending</option><option value="verified">Verified</option><option value="suspended">Suspended</option>
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="school-save-btn">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
